import { currencyChipHtml, escapeHtml, flagSvg, formatNum, type MoneyCurrency } from '@colplat/ui-web';
import {
  accountHistories, byCurrencyRank, currencyRank, NO_WALLET, UNASSIGNED_COLOUR, walletColours,
  type AccountHistories, type AccountHistory, type Ledger,
} from '@colplat/app-webledger/domain';
import {
  accountPointAt, dayAt, sparkSpan, stackedSparkSvg, wireSparkScroll,
  type SparkScroll, type SparkSpan,
} from './webledgerSparkline.js';

/**
 * The chart column on the chart of accounts: each account's balance at the
 * end of every day, one small chart per currency, with the bands showing
 * whose money it was.
 *
 * The wallet page answers "how much does this wallet have, and where did it
 * come from". This is the other way round — the account is fixed, and the
 * question is which wallets its balance belongs to — which is the question
 * ADR-0023 made answerable by putting the wallet on the leg rather than in
 * the account's name.
 *
 * Everything that has to agree with the wallet charts comes from the same
 * place: the span, the step shape, the zero line, and how scrolling, the
 * wheel and dragging behave (`webledgerSparkline.ts`).
 */

/**
 * How money is laid out wherever it is split by wallet and currency: each
 * wallet's order, colour and name, and the order of the currencies.
 */
export interface WalletKeys {
  colourOf(key: string): string;
  nameOf(key: string): string;
  /** Wallets in the wallet list's order, then the no-wallet key — the legend's order. */
  keys: string[];
  /** Sorts currency codes into the order the Settings page gives them (`currencyRank`). */
  byCurrency(a: string, b: string): number;
}

export interface AccountChart extends WalletKeys {
  histories: AccountHistories;
  span: SparkSpan;
  /** The date being inspected, drawn as a line through every chart. */
  markDay?: string;
}

/**
 * An account's balances at the end of `day`, per currency — what its Total
 * said on that evening. The last point at or before the day, because a
 * balance holds until something moves it.
 */
export function balancesOn(series: readonly AccountHistory[] | undefined, day: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of series ?? []) {
    const p = accountPointAt(s, day);
    if (p) out[s.currencyCode] = p.total;
  }
  return out;
}

/**
 * Every date on which some account's balance changed, in order — the stops
 * the arrow keys step between while a date is being inspected.
 *
 * Own balances, because a subtree's total changes exactly when one of its
 * accounts' own does.
 */
export function changeDays(histories: AccountHistories): string[] {
  const days = new Set<string>();
  for (const series of histories.own.values()) for (const s of series) for (const p of s.points) days.add(p.date);
  return [...days].sort();
}

/**
 * Every wallet's colour, name and place in line, from the replica.
 *
 * One source for the accounts chart and the wallet log's book breakdown, so
 * a wallet is the same colour and in the same row in both.
 */
export function walletKeys(ledger: Ledger): WalletKeys {
  const wallets = ledger.wallets();
  const colours = walletColours(wallets);
  const names = new Map(wallets.map((w) => [w.id, w.name] as const));
  const ordered = [...wallets]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
    .map((w) => w.id);
  return {
    colourOf: (key) => (key === NO_WALLET ? UNASSIGNED_COLOUR : colours.get(key) ?? UNASSIGNED_COLOUR),
    // A leg can name a wallet this device no longer holds — deleted since,
    // or not yet synced. Said as unknown rather than silently as Unassigned,
    // which would be a different and false claim.
    nameOf: (key) => (key === NO_WALLET ? 'Unassigned' : names.get(key) ?? 'Unknown wallet'),
    keys: [...ordered, NO_WALLET],
    byCurrency: byCurrencyRank(currencyRank(ledger.currencies())),
  };
}

/** Everything the column needs, from the replica. `undefined` when there is nothing to draw. */
export function buildAccountChart(ledger: Ledger): AccountChart | undefined {
  const histories = accountHistories(ledger);
  const span = sparkSpan([...histories.subtree.values()]);
  if (!span) return undefined;
  return { histories, span, ...walletKeys(ledger) };
}

/**
 * Whether a series ever ended a day holding anything.
 *
 * Money that only passes through — the Safe's pounds on the evening they
 * were changed into forints — leaves a series that is zero on every point,
 * and a chart of nothing is a line of height spent saying so.
 */
function everHeld(series: AccountHistory): boolean {
  return series.points.some((p) => Object.values(p.parts).some((v) => Math.abs(Number(v)) > 0.00005));
}

/**
 * The Total cell with the charts in it: one line per currency, the figure
 * and then that currency's chart.
 *
 * In the Total column rather than a column of its own: the chart is the
 * same number over time, and side by side the figures and their charts sat a
 * cell apart, wrapping on different rules, with nothing lining one up with
 * the other. Stacked one currency per line, the chip is the label for the
 * chart beside it.
 *
 * A line whose currency has no total today — held once and spent since —
 * gets the chip's own mark (flag over code) where the chip would be, so it
 * still says which currency the chart is drawing without printing a 0.00
 * nobody asked for.
 *
 * Every line is right-aligned and every chart is the same width, so the
 * charts line up down the page whatever the figures before them are.
 *
 * `converted` is the converted total, when the totals are shown in one
 * currency: it goes on its own line above, because a single figure in euros
 * is not the label for a forint chart.
 */
export function chartTotalHtml(
  balances: Readonly<Record<string, string>>, series: readonly AccountHistory[] | undefined,
  chart: AccountChart, accountId: string, cut: 'subtree' | 'own',
  money: (code: string) => MoneyCurrency | undefined, converted?: string,
): string {
  const held = new Map((series ?? []).filter(everHeld).map((s) => [s.currencyCode, s] as const));
  const owing = Object.keys(balances).filter((c) => Math.abs(Number(balances[c])) > 0.00005);
  // In the Settings page's order, not the alphabet's: a book kept mostly in
  // forints wants its forint line first.
  const codes = [...new Set([...held.keys(), ...owing])].sort((a, b) => chart.byCurrency(a, b));
  if (codes.length === 0) return converted ?? '<span class="text-secondary">–</span>';
  const lines = codes.map((code) => {
    const figure = converted === undefined && owing.includes(code)
      ? chipFor(balances[code]!, code, money)
      : markFor(code);
    const s = held.get(code);
    const graph = s
      ? `<span class="wl-spark" data-acct="${escapeHtml(accountId)}" data-cut="${cut}" data-spark="${escapeHtml(code)}">`
        + `${stackedSparkSvg(s, chart.span, (k) => chart.colourOf(k), chart.markDay)}</span>`
      // Kept as a gap the width of a chart, so this figure lines up with the
      // ones above and below it rather than jumping to the right edge.
      : '<span class="wl-acct-tl__nochart" aria-hidden="true"></span>';
    return `<span class="wl-acct-tl">${figure}${graph}</span>`;
  }).join('');
  return (converted !== undefined ? `<span class="wl-acct-tl wl-acct-tl--conv">${converted}</span>` : '') + lines;
}

function chipFor(amount: string, code: string, money: (code: string) => MoneyCurrency | undefined): string {
  const currency = money(code);
  return currencyChipHtml(amount, code, currency ? { currency } : {});
}

/**
 * The flag-over-code mark a chip leads with, on its own.
 *
 * Bare rather than `currencyMarkHtml`, which wraps it in a chip: the chip
 * tally collects every `.cf-cur-chip[data-cur]` on the page as an amount,
 * and a label that it would try to add up is a bug waiting for a Cmd-click.
 */
function markFor(code: string): string {
  return `<span class="wl-acct-tl__mark" role="text" aria-label="${escapeHtml(`${code}, nothing held now`)}">`
    + `<span class="cf-cur-mark">${flagSvg(code)}<span class="cf-cur-code" aria-hidden="true">${escapeHtml(code)}</span></span></span>`;
}

/**
 * Which colour is which wallet, beside the switch that shows the column.
 *
 * The colour is how the chart tells wallets apart, so it must never be the
 * only place that says so (invariant #11): the names are here as text, and
 * the tooltip names every part again.
 */
export function legendHtml(chart: AccountChart): string {
  return `<span class="wl-acct-legend__lbl">Wallets:</span>${chart.keys.map((k) =>
    `<span class="wl-acct-legend__item"><span class="wl-acct-legend__sw" style="background:${escapeHtml(chart.colourOf(k))}"`
    + ` aria-hidden="true"></span>${escapeHtml(chart.nameOf(k))}</span>`).join('')}`;
}

/**
 * What the account held at the end of `date`, as one small table: wallets
 * down the side, currencies across the top, a total underneath.
 *
 * A table, not chips. The first version gave each currency a total chip and
 * then a chip per wallet, which repeated the flag and the code on every
 * line and, for a currency only one wallet held, printed the same figure
 * twice — thirteen chips for one evening in the Safe, reported as "way too
 * many". The currency is a column heading said once; the figures are just
 * figures.
 *
 * Every currency the account held that evening and not only the chart under
 * the pointer: the question is about the account on that day. A currency it
 * held nothing in is left out, and so is a wallet with nothing in any of
 * them, so the table is exactly the constellation and no larger.
 */
export function tipHtml(
  series: readonly AccountHistory[], date: string, chart: AccountChart,
  money: (code: string) => MoneyCurrency | undefined,
): string {
  const columns = series.flatMap((s) => {
    const p = accountPointAt(s, date);
    return p ? [{ currencyCode: s.currencyCode, total: p.total, parts: p.parts }] : [];
  });
  return constellationHtml(escapeHtml(date), columns, chart, money);
}

/**
 * The table itself: wallets down the side, currencies across, a total under.
 *
 * Shared by the accounts chart and the wallet log's book running total, so
 * "what was where" reads the same wherever it is asked. A currency holding
 * nothing, and a wallet holding nothing in any of them, are left out — the
 * table is exactly the constellation and no larger.
 *
 * `head` is HTML, already escaped by the caller.
 */
export function constellationHtml(
  head: string,
  positions: readonly { currencyCode: string; total: string; parts: Readonly<Record<string, string>> }[],
  wallets: WalletKeys, money: (code: string) => MoneyCurrency | undefined,
): string {
  const held = (v: string | undefined): boolean => Math.abs(Number(v ?? 0)) > 0.00005;
  // The Settings page's order, the same as the chart lines beside the figure.
  const columns = positions.filter((c) => Object.values(c.parts).some(held))
    .sort((a, b) => wallets.byCurrency(a.currencyCode, b.currencyCode));
  const title = `<span class="wl-spark__tipdate">${head}</span>`;
  if (columns.length === 0) return `${title}<span class="text-secondary">nothing yet</span>`;

  const figure = (amount: string, code: string): string => {
    const n = Number(amount);
    // The minus is written out; the colour only agrees with it (invariant #11).
    return `<td class="wl-acct-tip__n${n < 0 ? ' is-negative' : ''}">${escapeHtml(formatNum(amount, money(code) ?? { fraction: 2, code }))}</td>`;
  };
  // Every key that holds something, in the legend's order — and any the
  // order does not know (a wallet not yet synced here) after it, rather
  // than silently dropped from a total it is part of.
  const known = wallets.keys.filter((k) => columns.some((c) => held(c.parts[k])));
  const strays = [...new Set(columns.flatMap((c) => Object.keys(c.parts)))]
    .filter((k) => !wallets.keys.includes(k) && columns.some((c) => held(c.parts[k])));
  const rows = [...known, ...strays];
  const headRow = `<tr><th></th>${columns.map((c) => `<th scope="col"><span class="cf-cur-mark">${flagSvg(c.currencyCode)}`
    + `<span class="cf-cur-code">${escapeHtml(c.currencyCode)}</span></span></th>`).join('')}</tr>`;
  // No swatch where there is no colour: accounts, as rows of a wallet's
  // breakdown, are named but not coloured.
  const swatch = (k: string): string => (wallets.colourOf(k)
    ? `<span class="wl-acct-legend__sw" style="background:${escapeHtml(wallets.colourOf(k))}" aria-hidden="true"></span>` : '');
  const body = rows.map((k) => `<tr><th scope="row">${swatch(k)}${escapeHtml(wallets.nameOf(k))}</th>`
    + columns.map((c) => (held(c.parts[k]) ? figure(c.parts[k]!, c.currencyCode) : '<td></td>')).join('')
    + '</tr>').join('');
  // Only worth a row when it says something the rows above do not.
  const total = rows.length > 1
    ? `<tr class="wl-acct-tip__sum"><th scope="row">Total</th>${columns.map((c) => figure(c.total, c.currencyCode)).join('')}</tr>`
    : '';
  return `${title}<table class="wl-acct-tip__grid"><thead>${headRow}</thead><tbody>${body}${total}</tbody></table>`;
}

/** One tooltip for the page, made on first use. */
let tip: HTMLElement | undefined;

/**
 * Show the one constellation tooltip, above `anchor` where there is room and
 * below it where there is not, centred on the pointer and kept in the window.
 */
/** Whether the tooltip is pinned — said on it, so a reader knows why it stays and how to let go. */
let tipPinned = false;
export function setTipPinned(on: boolean): void { tipPinned = on; }

export function showTableTip(html: string, anchor: DOMRect, clientX: number): void {
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'wl-spark__tip wl-acct-tip';
    tip.hidden = true;
  }
  // Put back if something replaced the body since — otherwise every later
  // hover writes into an element nobody can see.
  if (!tip.isConnected) document.body.appendChild(tip);
  tip.innerHTML = html + (tipPinned ? '<span class="wl-tip-pin">📌 Pinned — P to release</span>' : '');
  tip.classList.toggle('is-pinned', tipPinned);
  tip.hidden = false;
  const top = anchor.top - tip.offsetHeight - 8;
  tip.style.top = `${top < 8 ? anchor.bottom + 8 : top}px`;
  tip.style.left = `${Math.max(8, Math.min(clientX - tip.offsetWidth / 2, window.innerWidth - tip.offsetWidth - 8))}px`;
}

export function hideTableTip(): void {
  if (tip) tip.hidden = true;
}
/** Whether the charts drawn last in a container are being dragged. Replaced on each render. */
const dragging = new WeakMap<HTMLElement, () => boolean>();

/**
 * Scroll the charts drawn in a container together, after a render.
 *
 * Per render, because the charts are new elements every time. The hover
 * that reads them is `wireAccountHover`, wired once.
 */
export function wireAccountCharts(container: HTMLElement, scroll: SparkScroll): void {
  const boxes = [...container.querySelectorAll<HTMLElement>('.wl-spark[data-acct]')];
  const scrolling = wireSparkScroll(container, boxes, scroll);
  dragging.set(container, () => scrolling.isDragging());
}

/** What the accounts page hands the hover, read at the moment of hovering. */
export interface AccountHoverSource {
  /** The chart column, when it is on. */
  chart(): AccountChart | undefined;
  /** Every account's history — the chart's own when it is on, computed on demand when it is not. */
  histories(): AccountHistories;
  keys(): WalletKeys;
  nameOf(accountId: string): string;
  /** Whether a chip is the account's own balance or its whole subtree's. */
  cutOf(chip: HTMLElement): 'own' | 'subtree';
  /** The date being inspected, if one is: every breakdown answers for it rather than for the pointer or today. */
  inspectDay(): string | undefined;
  money(code: string): MoneyCurrency | undefined;
}

/**
 * The positions an account holds now: each currency's last point.
 *
 * A history's last point is the balance as it stands, because a balance
 * holds until something moves it — the same rule the charts draw by.
 */
export function positionsNow(series: readonly AccountHistory[]): Array<{
  currencyCode: string; total: string; parts: Record<string, string>;
}> {
  return series.flatMap((s) => {
    const last = s.points[s.points.length - 1];
    return last ? [{ currencyCode: s.currencyCode, total: last.total, parts: last.parts }] : [];
  });
}

/**
 * One hover for the whole chart of accounts, wired once.
 *
 * Over a chart: the account on the day under the pointer. Over a total's
 * chip — a currency chip, or the converted one — the same table for the
 * account as it stands now. Anything else hides it.
 *
 * One handler rather than one per kind, because each would hide the other's
 * tooltip the moment the pointer was not over its own kind of thing.
 */
/** What a page's hover can be asked to do from outside it. */
export interface HoverControl {
  /** Answer again for what is shown — after a redraw, or a step through dates. */
  refresh(): void;
  /** Pin what is shown now, or let go of what is pinned. Returns whether it is pinned after. */
  togglePin(): boolean;
  unpin(): void;
}

/**
 * Hover state any page of charts can share: what the tooltip is showing,
 * kept as a selector that finds it again after a redraw, and whether it is
 * pinned there.
 *
 * Pinned, it ignores the mouse: moving off it, or to another window, leaves
 * it where it is — what tracing a transaction across windows needs. A step
 * through dates still updates it, for the same chart or chip, because the
 * selector finds that one again in the redrawn page.
 */
export function hoverState(
  container: HTMLElement,
  update: (target: HTMLElement | null, clientX: number) => string | undefined,
): HoverControl & { onMove(target: HTMLElement, x: number, y: number): void; onLeave(): void } {
  let last: { x: number; y: number } | undefined;
  let shown: string | undefined;
  let pinned = false;
  const answerFor = (target: HTMLElement | null, x: number): void => { shown = update(target, x); };
  return {
    onMove: (target, x, y) => {
      if (pinned) return;
      last = { x, y };
      answerFor(target, x);
    },
    onLeave: () => {
      if (pinned) return;
      last = undefined;
      shown = undefined;
      hideTableTip();
    },
    refresh: () => {
      if (pinned) {
        const el = shown ? container.querySelector<HTMLElement>(shown) : null;
        if (!el) { pinned = false; setTipPinned(false); hideTableTip(); return; }
        const r = el.getBoundingClientRect();
        answerFor(el, r.left + r.width / 2);
        return;
      }
      if (!last || !tip || tip.hidden) return;
      const under = document.elementFromPoint(last.x, last.y) as HTMLElement | null;
      if (!under || !container.contains(under)) { hideTableTip(); return; }
      answerFor(under, last.x);
    },
    togglePin() {
      if (pinned) { this.unpin(); return false; }
      if (!shown || !tip || tip.hidden) return false;
      pinned = true;
      setTipPinned(true);
      this.refresh();
      return true;
    },
    unpin: () => {
      if (!pinned) return;
      pinned = false;
      setTipPinned(false);
      shown = undefined;
      hideTableTip();
    },
  };
}

export function wireAccountHover(container: HTMLElement, src: AccountHoverSource): HoverControl {
  /** Returns a selector that finds what it showed again, or undefined when it showed nothing. */
  const update = (target: HTMLElement | null, clientX: number): string | undefined => {
    const row = target?.closest<HTMLElement>('tr[data-row]');
    const accountId = row?.dataset['row'];

    const box = target?.closest<HTMLElement>('.wl-spark[data-acct]');
    if (box) {
      const chart = src.chart();
      if (!chart || dragging.get(container)?.()) { hideTableTip(); return undefined; }
      const cut = box.dataset['cut'] === 'own' ? chart.histories.own : chart.histories.subtree;
      const series = cut.get(box.dataset['acct'] ?? '') ?? [];
      const rect = box.getBoundingClientRect();
      const day = src.inspectDay() ?? dayAt(chart.span, clientX - rect.left + box.scrollLeft);
      showTableTip(tipHtml(series, day, chart, (code) => src.money(code)), rect, clientX);
      return `.wl-spark[data-acct="${CSS.escape(box.dataset['acct'] ?? '')}"][data-cut="${CSS.escape(box.dataset['cut'] ?? '')}"]`
        + `[data-spark="${CSS.escape(box.dataset['spark'] ?? '')}"]`;
    }

    // A total's chip: in the Total cell, or on the account's "own" line.
    const chip = target?.closest<HTMLElement>('td.wl-num .cf-cur-chip, .wl-acct-own .cf-cur-chip');
    if (chip && accountId) {
      const histories = src.histories();
      const own = src.cutOf(chip) === 'own';
      const series = (own ? histories.own : histories.subtree).get(accountId) ?? [];
      const day = src.inspectDay();
      const head = `${escapeHtml(src.nameOf(accountId))}${own ? ' · its own' : ''} · ${day ? escapeHtml(day) : 'now'}`;
      const positions = day
        ? series.flatMap((s) => {
          const p = accountPointAt(s, day);
          return p ? [{ currencyCode: s.currencyCode, total: p.total, parts: p.parts }] : [];
        })
        : positionsNow(series);
      showTableTip(constellationHtml(head, positions, src.keys(), (code) => src.money(code)),
        chip.getBoundingClientRect(), clientX);
      const where = chip.closest('.wl-acct-own') ? '.wl-acct-own' : 'td.wl-num';
      return `tr[data-row="${CSS.escape(accountId)}"] ${where} .cf-cur-chip[data-cur="${CSS.escape(chip.dataset['cur'] ?? '')}"]`;
    }
    hideTableTip();
    return undefined;
  };

  const state = hoverState(container, update);
  container.addEventListener('mousemove', (event) => state.onMove(event.target as HTMLElement, event.clientX, event.clientY));
  container.addEventListener('mouseleave', () => state.onLeave());
  return state;
}
